import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Upload, 
  FileText, 
  File, 
  Database, 
  Rss, 
  Globe, 
  CheckCircle, 
  Eye,
  Search,
  Newspaper,
  Link,
  BarChart3,
  Activity,
  Brain
} from 'lucide-react';
import URLScrapingPanel from '../components/data-ingestion/URLScrapingPanel';
import ExportPanel from '../components/data-ingestion/ExportPanel';
import DataStatusPanel from '../components/data-ingestion/DataStatusPanel';
import SearchPanel from '../components/data-ingestion/SearchPanel';
import DataInputCard from '../components/data-ingestion/DataInputCard';
import StatusCard from '../components/data-ingestion/StatusCard';
import ResultsCard from '../components/data-ingestion/ResultsCard';
import FileUploadInput from '../components/data-ingestion/FileUploadInput';
import RSSFeedsInput from '../components/data-ingestion/RSSFeedsInput';
import NewsArticleInput from '../components/data-ingestion/NewsArticleInput';
import MultiNewsInput from '../components/data-ingestion/MultiNewsInput';
import ProcessingControls from '../components/data-ingestion/ProcessingControls';

const DataUpload = () => {
  const { isDarkMode } = useTheme();
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [rssFeeds, setRssFeeds] = useState(['']);
  const [newsArticle, setNewsArticle] = useState({ headline: '', content: '', source: '' });
  const [uploadResults, setUploadResults] = useState([]);
  const [exportStatus, setExportStatus] = useState('');
  
  // Multi-news API state
  const [isCollectingMultiNews, setIsCollectingMultiNews] = useState(false);
  const [multiNewsResults, setMultiNewsResults] = useState(null);
  
  // New state for merged functionality
  const [databaseStats, setDatabaseStats] = useState({
    pinecone: {},
    local_database: {},
    overall: {},
    lastUpdated: null
  });
  const [searchResults, setSearchResults] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [isRunningPestleAnalysis, setIsRunningPestleAnalysis] = useState(false);
  
  const fileInputRef = useRef(null);
  const API_BASE_URL = 'http://localhost:8000/api/data-ingestion';

  // File type configurations
  const fileTypes = {
    pdf: { icon: FileText, color: 'red', maxSize: '50MB', description: 'PDF documents, reports, research papers' },
    excel: { icon: Database, color: 'green', maxSize: '25MB', description: 'Excel files (.xlsx, .xls) with data' },
    csv: { icon: Database, color: 'blue', maxSize: '25MB', description: 'CSV data files' },
    text: { icon: File, color: 'gray', maxSize: '10MB', description: 'Text files (.txt, .md)' },
    rss: { icon: Rss, color: 'orange', maxSize: 'N/A', description: 'RSS feed URLs' },
    news: { icon: Globe, color: 'purple', maxSize: 'N/A', description: 'Manual news article entry' },
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      type: getFileType(file.name),
      status: 'pending',
      progress: 0,
      error: null
    }));
    
    setUploadQueue(prev => [...prev, ...newFiles]);
  };

  const getFileType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (['xlsx', 'xls'].includes(ext)) return 'excel';
    if (ext === 'csv') return 'csv';
    if (['txt', 'md'].includes(ext)) return 'text';
    return 'unknown';
  };

  const removeFromQueue = (id) => {
    setUploadQueue(prev => prev.filter(item => item.id !== id));
  };

  const uploadFiles = async () => {
    setIsUploading(true);
    const results = [];

    for (const item of uploadQueue) {
      if (item.status === 'completed') continue;

      try {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'uploading', progress: 10 } : q
        ));

        const formData = new FormData();
        formData.append('file', item.file);
        formData.append('file_type', item.type);
        formData.append('source', 'Manual Upload');

        const response = await fetch(`${API_BASE_URL}/upload`, {
          method: 'POST',
          body: formData,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadQueue(prev => prev.map(q => 
              q.id === item.id ? { ...q, progress } : q
            ));
          }
        });

        if (response.ok) {
          const result = await response.json();
          setUploadQueue(prev => prev.map(q => 
            q.id === item.id ? { ...q, status: 'completed', progress: 100 } : q
          ));
          results.push({ 
            ...result, 
            filename: item.file.name, 
            original_file_path: result.original_file_path,
            storage: result.storage || {
              pinecone_vector_db: false,
              local_database: false,
              database_available: false
            },
            status: 'success' 
          });
        } else {
          const error = await response.text();
          setUploadQueue(prev => prev.map(q => 
            q.id === item.id ? { ...q, status: 'error', error } : q
          ));
          results.push({ filename: item.file.name, status: 'error', error });
        }
      } catch (error) {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'error', error: error.message } : q
        ));
        results.push({ filename: item.file.name, status: 'error', error: error.message });
      }
    }

    setUploadResults(results);
    setIsUploading(false);
  };

  const uploadRSSFeeds = async () => {
    setIsUploading(true);
    try {
      const validFeeds = rssFeeds.filter(feed => feed.trim() !== '');
      
      const response = await fetch(`${API_BASE_URL}/rss-feeds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feeds: validFeeds })
      });

      if (response.ok) {
        const result = await response.json();
        setUploadResults([{ ...result, type: 'rss', status: 'success' }]);
        setRssFeeds(['']);
      } else {
        const error = await response.text();
        setUploadResults([{ type: 'rss', status: 'error', error }]);
      }
    } catch (error) {
      setUploadResults([{ type: 'rss', status: 'error', error: error.message }]);
    }
    setIsUploading(false);
  };

  const uploadNewsArticle = async () => {
    if (!newsArticle.headline || !newsArticle.content) {
      alert('Please fill in both headline and content');
      return;
    }

    setIsUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/manual-article`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newsArticle)
      });

      if (response.ok) {
        const result = await response.json();
        setUploadResults([{ ...result, type: 'news', status: 'success' }]);
        setNewsArticle({ headline: '', content: '', source: '' });
      } else {
        const error = await response.text();
        setUploadResults([{ type: 'news', status: 'error', error }]);
      }
    } catch (error) {
      setUploadResults([{ type: 'news', status: 'error', error: error.message }]);
    }
    setIsUploading(false);
  };


  const addRSSFeed = () => {
    setRssFeeds(prev => [...prev, '']);
  };

  const updateRSSFeed = (index, value) => {
    setRssFeeds(prev => prev.map((feed, i) => i === index ? value : feed));
  };

  const removeRSSFeed = (index) => {
    setRssFeeds(prev => prev.filter((_, i) => i !== index));
  };

  const handleExport = async (format, result) => {
    setExportStatus(`Export completed: ${result.records_exported} records exported to ${result.file_path}`);
  };

  // New functions for merged functionality
  const fetchDatabaseStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/stats`);
      if (response.ok) {
        const data = await response.json();
        setDatabaseStats({
          ...data.stats,
          lastUpdated: new Date().toLocaleString()
        });
      }
    } catch (error) {
      console.error('Error fetching database stats:', error);
    }
  };

  const handleDataRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await fetch(`${API_BASE_URL}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setExportStatus(`Data refresh completed: ${result.message}`);
        await fetchDatabaseStats(); // Refresh stats
      } else {
        setExportStatus('Error refreshing data');
      }
    } catch (error) {
      setExportStatus(`Error: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSearch = async (query, dataType = 'analyzed', limit = 10) => {
    try {
      const response = await fetch(`${API_BASE_URL}/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query, data_type: dataType, top_k: limit })
      });
      
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      }
    } catch (error) {
      console.error('Error searching data:', error);
    }
  };

  const processScrapedFiles = async () => {
    setProcessingStatus('Processing scraped files...');
    try {
      const response = await fetch(`${API_BASE_URL}/process-scraped-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setProcessingStatus(`Scraped files processed: ${result.statistics.successful} successful, ${result.statistics.skipped} skipped`);
        await fetchDatabaseStats(); // Refresh stats
      } else {
        setProcessingStatus('Error processing scraped files');
      }
    } catch (error) {
      setProcessingStatus(`Error: ${error.message}`);
    }
  };

  const reprocessPestleScores = async () => {
    setProcessingStatus('Reprocessing PESTLE scores...');
    try {
      const response = await fetch(`${API_BASE_URL}/reprocess-pestle-scores`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setProcessingStatus(`PESTLE scores updated: ${result.statistics.successful} articles reprocessed`);
        await fetchDatabaseStats(); // Refresh stats
      } else {
        setProcessingStatus('Error reprocessing PESTLE scores');
      }
    } catch (error) {
      setProcessingStatus(`Error: ${error.message}`);
    }
  };

  const runPestleAnalysis = async () => {
    setIsRunningPestleAnalysis(true);
    setProcessingStatus('Running PESTLE analysis on unanalyzed articles...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/run-pestle-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        setProcessingStatus(`PESTLE analysis completed: ${result.message}`);
        await fetchDatabaseStats(); // Refresh stats
      } else {
        const errorResult = await response.json();
        setProcessingStatus(`PESTLE analysis failed: ${errorResult.detail || 'Unknown error'}`);
      }
    } catch (error) {
      setProcessingStatus(`Error running PESTLE analysis: ${error.message}`);
    } finally {
      setIsRunningPestleAnalysis(false);
    }
  };

  const collectMultiNews = async (requestData) => {
    setIsCollectingMultiNews(true);
    setMultiNewsResults(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/multi-news-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setMultiNewsResults(result);
        await fetchDatabaseStats(); // Refresh stats
      } else {
        const errorResult = await response.json();
        setMultiNewsResults({
          success: false,
          error: errorResult.detail || 'Unknown error occurred'
        });
      }
    } catch (error) {
      setMultiNewsResults({
        success: false,
        error: `Error collecting multi-news: ${error.message}`
      });
    } finally {
      setIsCollectingMultiNews(false);
    }
  };

  // Load database stats on component mount
  useEffect(() => {
    fetchDatabaseStats();
  }, []);


  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Data Upload & Processing Center</h1>
        <p className="text-gray-600 dark:text-gray-300">
          Upload, process, and manage various data sources for critical minerals analysis
        </p>
      </div>

      {/* System Status and Processing Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Data Status Panel */}
        <StatusCard
          title="System Status"
          icon={Activity}
          iconColor="green"
        >
          <DataStatusPanel 
            stats={databaseStats}
            onRefresh={fetchDatabaseStats}
          />
        </StatusCard>

        {/* Processing Controls Panel */}
        <StatusCard
          title="Processing Controls"
          icon={Brain}
          iconColor="purple"
        >
          <ProcessingControls
            onProcessScrapedFiles={processScrapedFiles}
            onRunPestleAnalysis={runPestleAnalysis}
            onReprocessPestleScores={reprocessPestleScores}
            onDataRefresh={handleDataRefresh}
            isRefreshing={isRefreshing}
            isRunningPestleAnalysis={isRunningPestleAnalysis}
            processingStatus={processingStatus}
          />
        </StatusCard>
      </div>

      {/* File Upload Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* File Upload Card */}
        <DataInputCard
          title="File Upload"
          icon={Upload}
          iconColor="blue"
        >
          <FileUploadInput
            uploadQueue={uploadQueue}
            onFileSelect={handleFileSelect}
            onRemoveFromQueue={removeFromQueue}
            onUploadFiles={uploadFiles}
            isUploading={isUploading}
            fileTypes={fileTypes}
          />
        </DataInputCard>

        {/* RSS Feeds Card */}
        <DataInputCard
          title="RSS Feeds"
          icon={Rss}
          iconColor="orange"
          description="Add RSS feed URLs to automatically collect news articles"
        >
          <RSSFeedsInput
            rssFeeds={rssFeeds}
            onAddFeed={addRSSFeed}
            onUpdateFeed={updateRSSFeed}
            onRemoveFeed={removeRSSFeed}
            onUploadFeeds={uploadRSSFeeds}
            isUploading={isUploading}
          />
        </DataInputCard>
      </div>

      {/* URL Scraping Section */}
      <DataInputCard
        title="URL Content Scraping"
        icon={Link}
        iconColor="green"
      >
        <URLScrapingPanel apiBaseUrl={API_BASE_URL} />
      </DataInputCard>

      {/* Manual News Article Entry */}
      <DataInputCard
        title="Manual News Article Entry"
        icon={Globe}
        iconColor="purple"
      >
        <NewsArticleInput
          newsArticle={newsArticle}
          onUpdateArticle={(field, value) => setNewsArticle(prev => ({ ...prev, [field]: value }))}
          onUploadArticle={uploadNewsArticle}
          isUploading={isUploading}
        />
      </DataInputCard>


      {/* Multi-News API Integration */}
      <DataInputCard
        title="Multi-News API Collection"
        icon={Globe}
        iconColor="green"
        description="Collect news from multiple APIs (GNews, MediaStack, NewsData.io, WorldNews) with comprehensive strategies"
      >
        <MultiNewsInput
          onCollectNews={collectMultiNews}
          isCollecting={isCollectingMultiNews}
          collectionResults={multiNewsResults}
        />
      </DataInputCard>

      {/* Upload Results */}
      <ResultsCard
        title="Upload Results"
        icon={CheckCircle}
        iconColor="green"
        results={uploadResults}
      />

      {/* Search and Export Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Panel */}
        <StatusCard
          title="Search Data"
          icon={Search}
          iconColor="blue"
        >
          <SearchPanel 
            onSearch={handleSearch}
            results={searchResults}
            apiBaseUrl={API_BASE_URL}
          />
        </StatusCard>

        {/* Export Panel */}
        <StatusCard
          title="Export Data"
          icon={BarChart3}
          iconColor="indigo"
        >
          <ExportPanel 
            onExport={handleExport}
            status={exportStatus}
            apiBaseUrl={API_BASE_URL}
          />
        </StatusCard>
      </div>

      {/* Instructions */}
      <StatusCard
        title="How It Works"
        icon={Eye}
        iconColor="gray"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="bg-blue-100 dark:bg-blue-900/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Upload className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">1. Upload Data</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Upload files, add RSS feeds, scrape URLs, or manually enter news articles
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-green-100 dark:bg-green-900/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Database className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">2. Auto Processing</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Our NLP engine automatically analyzes and categorizes the content
            </p>
          </div>
          
          <div className="text-center">
            <div className="bg-purple-100 dark:bg-purple-900/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">3. Ready for Analysis</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Processed data becomes available in dashboards and analysis tools
            </p>
          </div>
        </div>
      </StatusCard>
    </div>
  );
};

export default DataUpload;